import React, { useContext, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import { ArrowUp, ArrowDown, User, Trash2, Plus, Phone, ExternalLink } from "lucide-react";

import SharedDataContext from "../../../SharedDataContext";
import { useTableData } from "../../../hooks/useTableData";
import { getTelephonyUsers, updateUserRecordingSettings } from "../../../apis/phoneInventory";
import { returnArray, isEmptyObject } from "../../../utils/GenericFn";

import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Switch } from "../../ui/switch";
import { Checkbox } from "../../ui/checkbox";
import { useNavigate, useParams, Link } from "react-router-dom";
import TableLoader from "../../Loaders/TableLoader";
import NoData from "../../NoData";
import Pagination from "../../Pagination";
import Filter from "../../Filter";
import EditColumn from "../../EditColumn";
import Text from "../../Tags/Text";
import RemoveDialerAccessModal from "./modals/RemoveDialerAccessModal";
import DialerAccessEmptyState from "./DialerAccessEmptyState";
import PageHeader from "../../PageHeader";
import { getCountryFlag } from "../../../constants/countries";
import TooltipWrapper from "../../TooltipWrapper";
import Flag from "../../Flag";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function DialerAIContainer() {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const navigate = useNavigate();
  const { workspaceSlug } = useParams();
  const captureEvent = useCaptureEvent();

  const queryClient = useQueryClient();
  const [selectedRows, setSelectedRows] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [updatingMembers, setUpdatingMembers] = useState(new Set()); // Track which members are being updated
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const {
    data,
    paginationInfo,
    filterMetadata,
    columnMetadata,
    apiResponse,
    isLoading,
    isFetching,
    isFetched,
    isSuccess,
    isError,
    filters,
    sort,
    pagination,
    columns,
    sortedVisibleColumns,
    getPageNumber,
    getFiltersObject,
    handleSetSort,
    getColumns,
    refetchData,
  } = useTableData({
    queryKey: "get_dialer_ai_users",
    fetchFn: getTelephonyUsers,
    defaultHeaders: { "X-Workspace-ID": activeWorkspace?.id },
    enabled: !!activeWorkspace?.id,
    onSuccess: (data) => {
      setIsInitialLoad(false);
    },
    onError: (error) => {
      console.error("Dialer AI users API error:", error);
      setIsInitialLoad(false);
    },
  });

  // Format date to "12th Jan, 2024" format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();

    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return "th";
      switch (day % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${day}${getOrdinalSuffix(day)} ${month}, ${year}`;
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  };

  const processedData =
    data?.map((item) => ({
      ...item,

      assigned_numbers_count: Array.isArray(item.assigned_numbers) ? item.assigned_numbers.length : 0,
      // Format created date for display
      created_at_display: formatDate(item.created_at),
    })) || [];

  // Handle workspace changes
  useEffect(() => {
    setIsInitialLoad(true);
  }, [workspaceSlug]);

  useEffect(() => {
    if (activeWorkspace?.id) {
      captureEvent("SET_DialerAI_UserAccessView", {
        workspace_id: activeWorkspace.id,
      });
    }
  }, [activeWorkspace?.id, captureEvent]);

  // Mutation for updating member recording status
  const updateCallRecordingMutation = useMutation(updateUserRecordingSettings, {
    onSuccess: (data, variables) => {
      // Remove from updating set
      setUpdatingMembers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.userId);
        return newSet;
      });

      // Show success toast
      toast.success(`Recording ${variables.data.recording_enabled ? "enabled" : "disabled"} for user`);

      // Invalidate and refetch the query
      queryClient.invalidateQueries("get_dialer_ai_users");
      captureEvent("SET_DialerAI_RecordingToggleSuccess", {
        user_id: variables.userId,
        enabled: variables.data.recording_enabled,
      });
    },
    onError: (error, variables) => {
      console.error("Error updating user recording:", error);

      // Remove from updating set on error
      setUpdatingMembers((prev) => {
        const newSet = new Set(prev);
        newSet.delete(variables.userId);
        return newSet;
      });

      // Show error toast
      toast.error("Failed to update recording setting. Please try again.");
      captureEvent("SET_DialerAI_RecordingToggleFailure", {
        user_id: variables.userId,
        enabled: variables.data.recording_enabled,
        error: error?.message || "unknown_error",
      });
    },
  });

  const handleSelectAll = () => {
    setSelectedRows(
      selectedRows.length === processedData?.length ? [] : processedData.map((item) => item.id)
    );
  };

  const handleSelectRow = (isChecked, id) => {
    setSelectedRows((prev) => (isChecked ? [...prev, id] : prev.filter((rowId) => rowId !== id)));
  };

  const handleRecordingToggle = (userId, currentStatus) => {
    // Add to updating set
    setUpdatingMembers((prev) => new Set(prev.add(userId)));

    // Call the mutation with new API signature
    captureEvent("SET_DialerAI_RecordingToggleClick", {
      user_id: userId,
      enabled: !currentStatus,
    });
    updateCallRecordingMutation.mutate({
      userId,
      data: { recording_enabled: !currentStatus },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const handleDeleteMember = (member) => {
    setUserToDelete(member);
    setIsRemoveModalOpen(true);
    captureEvent("SET_DialerAI_RemoveUserOpen", {
      user_id: member?.id,
    });
  };

  const handleRemoveModalClose = () => {
    setIsRemoveModalOpen(false);
    setUserToDelete(null);
  };

  const handleRemoveSuccess = () => {
    // Refresh the table data
    queryClient.invalidateQueries("get_dialer_ai_users");
    captureEvent("SET_DialerAI_RemoveUserSuccess", {
      user_id: userToDelete?.id,
    });
  };

  const handleAddUser = () => {
    navigate(`/${workspaceSlug}/dialer-ai/add-user`);
    captureEvent("SET_DialerAI_AddUserClick");
  };

  const renderAssignedNumbers = (assignedNumbers) => {
    const numbers = Array.isArray(assignedNumbers) ? assignedNumbers : [];
    if (numbers.length === 0) return <span className="text-gray-400">None</span>;

    const firstNumber = numbers[0];
    const remainingCount = numbers.length - 1;

    // Tooltip content showing all numbers and their assigned dates
    const tooltipContent = (
      <div className="p-1 min-w-[300px]">
        <div className="grid grid-cols-[1.5fr,1fr] gap-4 mb-2 pb-2 border-b border-gray-700">
          <div className="text-xs font-medium text-gray-400">Number</div>
          <div className="text-xs font-medium text-gray-400">Assigned at</div>
        </div>
        {numbers.map((number, index) => (
          <div key={index} className="grid grid-cols-[1.5fr,1fr] gap-4 py-1.5 items-center">
            <div className="flex items-center gap-2">
              <span className="text-[20px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border shrink-0">
                <Flag code={number?.country} squared />
              </span>
              <span className="text-xs text-white">{number.phone_number}</span>
              <Link
                to={`/${workspaceSlug}/numbers-inventory/${number?.id}/settings`}
                className="text-gray-400 hover:text-white transition-colors"
                title="Detailed View"
              >
                <ExternalLink className="w-3 h-3" />
              </Link>
            </div>
            <div className="text-xs text-white">
              {formatShortDate(number?.assigned_at)}
            </div>
          </div>
        ))}
      </div>
    );

    return (
      <div className="flex items-center gap-2">
        {/* Display first number with country flag - wrapped in tooltip */}
        <TooltipWrapper
          content={tooltipContent}
          className="flex items-center gap-1 cursor-pointer"
        >
          <span className="text-[24px] rounded-full inline-flex items-center justify-center overflow-hidden border border-border mr-1">
            <Flag code={firstNumber?.country} squared />
          </span>
          <span className="text-sm text-[#101828] dark:text-white font-normal">
            {firstNumber.phone_number}
          </span>
        </TooltipWrapper>

        {/* Show +count for additional numbers with hover tooltip */}
        {remainingCount > 0 && (
          <div className="relative group">
            <TooltipWrapper
              content={tooltipContent}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600 cursor-pointer hover:bg-gray-200 transition-colors"
            >
              +{remainingCount}
            </TooltipWrapper>
          </div>
        )}
      </div>
    );
  };

  const renderRecordingEnabled = (item) => {
    const isUpdating = updatingMembers.has(item.id);

    return (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Switch
          checked={item.recording_enabled}
          onCheckedChange={() => handleRecordingToggle(item.id, item.recording_enabled)}
          disabled={isUpdating}
          className={`data-[state=checked]:bg-[#7F56D9] data-[state=unchecked]:bg-gray-300 ${
            isUpdating ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />
        {isUpdating && (
          <div className="inline-flex items-center">
            <div className="loader loader_circle w-3 h-3"></div>
          </div>
        )}
      </div>
    );
  };

  const renderCellContent = (columnKey, item) => {
    switch (columnKey) {
      case "name":
        return (
          <div className="flex items-center">
            {/* <div className="flex-shrink-0 h-10 w-10">
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-5 w-5 text-blue-600" />
              </div>
            </div> */}

            <div className="text-sm font-medium text-[#181D27] dark:text-white">{item.name}</div>
          </div>
        );

      case "email":
        return <div className="text-sm text-[#535862] font-normal dark:text-white">{item.email}</div>;

      case "recording_enabled":
        return renderRecordingEnabled(item);

      case "assigned_numbers":
        return renderAssignedNumbers(item.assigned_numbers);

      case "assigned_numbers_count":
        return renderAssignedNumbers(item.assigned_numbers);

      case "created_at":
        return <div className="text-sm text-[#535862] dark:text-white">{item.created_at_display}</div>;

      default:
        return <div className="text-sm text-gray-900 dark:text-white">{item?.[columnKey] || "-"}</div>;
    }
  };

  const shouldShowEmptyState =
    !isFetching && isSuccess && !isInitialLoad && Array.isArray(data) && data.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader
        heading="User Access"
        description={`Add or remove user access to ${process.env.REACT_APP_APP_NAME} sales dialer`}
        cta={
          <>
            {!shouldShowEmptyState && (
              <div>
                <Button onClick={handleAddUser} className="bg-[#7F56D9] hover:bg-[#6D47C7] text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add User
                </Button>
              </div>
            )}
          </>
        }
      />

      {data && (data.length > 0 || !isEmptyObject(filters)) && (
        <div className="flex justify-between items-center">
          <div className="space-x-3 inline-flex items-center">
            {filterMetadata && (
              <>
                <span className="text-sm text-gray-500">Filters:</span>
                <Filter filterMetadata={filterMetadata} getFiltersObject={getFiltersObject} />
              </>
            )}
          </div>
          <div className="space-x-3">
            {columnMetadata && <EditColumn columnMetadata={columnMetadata} getColumns={getColumns} />}
          </div>
        </div>
      )}

      {/* Table */}
      <div>
        {processedData && processedData.length > 0 && isSuccess && (
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden border border-border rounded-[--radius]">
                  <table className="min-w-full divide-y divide-border">
                    <thead className="bg-background">
                      <tr>
                        {returnArray(sortedVisibleColumns).map((column, index) => (
                          <th
                            key={index}
                            scope="col"
                            className={`px-3 py-3.5 text-left text-xs font-medium text-text-600 ${
                              column.sortable ? "cursor-pointer" : ""
                            }`}
                            onClick={() => (column.sortable ? handleSetSort(column.key) : void 0)}
                          >
                            <span className={"inline-flex items-center"}>
                              {column.display_name}
                              {column.sortable && (
                                <>
                                  {sort === `-${column.key}` ? (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3" />
                                  ) : sort === column.key ? (
                                    <ArrowUp className="inline-block ml-1 h-3 w-3" />
                                  ) : (
                                    <ArrowDown className="inline-block ml-1 h-3 w-3 opacity-50" />
                                  )}
                                </>
                              )}
                            </span>
                          </th>
                        ))}
                        <th scope="col" className="!px-3 !py-4 text-left text-sm font-medium" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border bg-background">
                      {processedData &&
                        processedData.length > 0 &&
                        returnArray(processedData).map((item, index) => (
                          <tr
                            key={item.id}
                            className={`hover:bg-gray-50 cursor-pointer ${index % 2 !== 0 ? "" : ""}`}
                          >
                            {returnArray(sortedVisibleColumns).map(({ key: colKey }) => (
                              <td
                                key={colKey}
                                className="whitespace-nowrap px-4 text-sm text-gray-900 dark:text-[#F7F7F7] "
                              >
                                {renderCellContent(colKey, item)}
                              </td>
                            ))}
                            <td className="whitespace-nowrap px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-gray-400 hover:text-red-600"
                                onClick={() => handleDeleteMember(item)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {(isLoading || isInitialLoad) && <TableLoader />}

        {isError && (
          <NoData title="Error loading dialer users" description="Please try again or contact support" />
        )}

        {shouldShowEmptyState && <DialerAccessEmptyState />}

        {/* Pagination */}
        <div className="flex items-center justify-between rounded-[--radius] py-3">
          <div className="flex-1">
            {paginationInfo && paginationInfo.total > 0 && (
              <Pagination data={paginationInfo} getPageNumber={getPageNumber} />
            )}
          </div>
        </div>
      </div>

      {/* Remove Dialer Access Modal */}
      <RemoveDialerAccessModal
        isOpen={isRemoveModalOpen}
        onClose={handleRemoveModalClose}
        userToDelete={userToDelete}
        activeWorkspace={activeWorkspace}
        onSuccess={handleRemoveSuccess}
      />
    </div>
  );
}
